import React, { useState, useEffect, useCallback } from 'react';
import { X, Play, Trash2, Calendar, Globe, AlertCircle, Loader2 } from 'lucide-react';
// NEW: Import campaignAPI directly instead of dynamic import
import { campaignAPI } from '../api';

const LoadCampaignModal = ({ isOpen, onClose, onLoad, userId }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the modular API directly
      const data = await campaignAPI.getCampaigns(userId);
      
      // Handle both array response and object with campaigns property
      setCampaigns(Array.isArray(data) ? data : (data.campaigns || []));
    } catch (err) {
      setError(err.message);
      console.error('Error fetching campaigns:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      fetchCampaigns();
    }
  }, [isOpen, userId, fetchCampaigns]);

  const handleLoadCampaign = (campaign) => {
    onLoad(campaign);
    onClose();
  };

  const handleDeleteCampaign = async (campaignId, e) => {
    e.stopPropagation();
    
    try {
      // Use the modular API directly
      await campaignAPI.deleteCampaign(campaignId);
      
      // Remove from local state
      setCampaigns(campaigns.filter(c => c.id !== campaignId));
      setShowDeleteConfirm(null);
    } catch (err) {
      setError(err.message);
      console.error('Error deleting campaign:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getThemeColor = (theme) => {
    const colors = {
      vanilla: 'text-blue-400',
      majestic: 'text-emerald-400',
      devastators: 'text-amber-400',
      archbound: 'text-purple-400',
    };
    return colors[theme] || 'text-blue-400';
  };

  const getThemeBorder = (theme) => {
    const colors = {
      vanilla: 'border-blue-500',
      majestic: 'border-emerald-500',
      devastators: 'border-amber-500',
      archbound: 'border-purple-500',
    };
    return colors[theme] || 'border-blue-500';
  };

  const getThemeLabel = (theme) => {
    const labels = {
      vanilla: 'LAST STAND',
      majestic: 'MAJESTIC',
      devastators: 'DEVASTATORS',
      archbound: 'ARCHBOUND',
    };
    return labels[theme] || theme.toUpperCase();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-gray-900 border-2 border-cyan-500 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl shadow-cyan-500/20">
        {/* Header */}
        <div className="bg-gray-800 border-b-2 border-cyan-500 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Play className="text-cyan-400" size={28} />
            <h2 className="text-3xl font-bold text-white tracking-wider">LOAD CAMPAIGN</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="text-cyan-400 animate-spin mb-4" size={48} />
              <p className="text-gray-400 font-mono">LOADING CAMPAIGNS...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="text-red-400" size={24} />
                <div>
                  <h3 className="text-red-400 font-bold">ERROR</h3>
                  <p className="text-gray-300 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && campaigns.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <Globe className="text-gray-600 mb-4" size={64} />
              <h3 className="text-xl font-bold text-gray-400 mb-2">NO CAMPAIGNS FOUND</h3>
              <p className="text-gray-500 font-mono text-sm">Start a new campaign to choose your universe</p>
            </div>
          )}

          {!loading && !error && campaigns.length > 0 && (
            <div className="space-y-4">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className={`group relative bg-gray-800/50 border-2 ${getThemeBorder(campaign.theme)} rounded-lg p-6 transition-all duration-300 hover:bg-gray-800/80 hover:shadow-xl ${getThemeColor(campaign.theme).replace('text-', 'hover:shadow-')}/20 cursor-pointer`}
                  onClick={() => setSelectedCampaign(campaign.id)}
                >
                  {/* Delete confirmation overlay */}
                  {showDeleteConfirm === campaign.id && (
                    <div className="absolute inset-0 bg-gray-900/95 rounded-lg flex items-center justify-center z-10">
                      <div className="text-center p-6">
                        <AlertCircle className="text-red-400 mx-auto mb-3" size={48} />
                        <h3 className="text-xl font-bold text-white mb-2">DELETE CAMPAIGN?</h3>
                        <p className="text-gray-400 text-sm mb-6">This action cannot be undone</p>
                        <div className="flex gap-3 justify-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeleteConfirm(null);
                            }}
                            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded font-mono transition-colors"
                          >
                            CANCEL
                          </button>
                          <button
                            onClick={(e) => handleDeleteCampaign(campaign.id, e)}
                            className="px-6 py-2 bg-red-600 hover:bg-red-500 text-white rounded font-mono transition-colors"
                          >
                            DELETE
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className={`text-2xl font-bold ${getThemeColor(campaign.theme)}`}>
                          {campaign.name}
                        </h3>
                        <span className={`px-3 py-1 rounded text-xs font-bold border-2 ${getThemeBorder(campaign.theme)} ${getThemeColor(campaign.theme)} uppercase tracking-wider`}>
                          {getThemeLabel(campaign.theme)}
                        </span>
                      </div>

                      {campaign.description && (
                        <p className="text-gray-300 mb-4 text-sm">
                          {campaign.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-4 text-sm text-gray-400 font-mono">
                        <div className="flex items-center gap-2">
                          <Calendar size={16} />
                          <span>Created: {formatDate(campaign.created_at)}</span>
                        </div>
                        {campaign.updated_at && campaign.updated_at !== campaign.created_at && (
                          <div className="flex items-center gap-2">
                            <Calendar size={16} />
                            <span>Updated: {formatDate(campaign.updated_at)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadCampaign(campaign);
                        }}
                        className={`px-6 py-3 ${getThemeColor(campaign.theme).replace('text-', 'bg-').replace('-400', '-600')} hover:${getThemeColor(campaign.theme).replace('text-', 'bg-').replace('-400', '-500')} text-white rounded font-bold transition-colors transform hover:scale-105`}
                      >
                        LOAD
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteConfirm(campaign.id);
                        }}
                        className="p-3 bg-red-900/30 hover:bg-red-900/50 border-2 border-red-500 text-red-400 rounded transition-colors"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Selection indicator */}
                  {selectedCampaign === campaign.id && (
                    <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${getThemeColor(campaign.theme).replace('text-', 'bg-')} animate-pulse`} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-800 border-t-2 border-cyan-500 p-4 flex justify-between items-center">
          <div className="text-xs text-gray-500 font-mono">
            {campaigns.length} CAMPAIGN{campaigns.length !== 1 ? 'S' : ''} AVAILABLE
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded font-mono transition-colors"
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoadCampaignModal;